if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileSwiperMql = window.matchMedia('(max-width: 989px)');
        this.mobileSwiper = null;
        this.handleMobileSwiperInit = this.handleMobileSwiperInit.bind(this);
        this.handleMobileSwiperDestroy = this.handleMobileSwiperDestroy.bind(this);
        this.handleMobileSwiperSlideChange = this.handleMobileSwiperSlideChange.bind(this);

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('mobileSwiper:init', this.handleMobileSwiperInit);
          this.elements.viewer.addEventListener('mobileSwiper:destroy', this.handleMobileSwiperDestroy);

          if (this.elements.viewer.__mobileSwiperInstance) {
            this.handleMobileSwiperInit({ detail: { swiper: this.elements.viewer.__mobileSwiperInstance } });
          }
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, fromMobileSwiper = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.setLoadingState(activeMedia);

        const useMobileSwiper = this.usingMobileSwiper();

        if (useMobileSwiper && !fromMobileSwiper) {
          this.slideMobileSwiperTo(mediaId);
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();

          if (this.mobileSwiper) {
            this.mobileSwiper.updateSlides && this.mobileSwiper.updateSlides();
            this.mobileSwiper.update && this.mobileSwiper.update();
          }
        }

        this.preventStickyHeader();
        if (!useMobileSwiper) {
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setLoadingState(activeMedia) {
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-loading');
        });

        const image = activeMedia.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const clearLoading = () => {
          activeMedia.classList.remove('is-loading');
          image.removeEventListener('load', clearLoading);
          image.removeEventListener('error', clearLoading);
        };

        if (image.complete && image.naturalWidth > 0) {
          clearLoading();
          return;
        }

        activeMedia.classList.add('is-loading');
        image.addEventListener('load', clearLoading, { once: true });
        image.addEventListener('error', clearLoading, { once: true });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      usingMobileSwiper() {
        return Boolean(this.mobileSwiper) && this.mobileSwiperMql.matches;
      }

      slideMobileSwiperTo(mediaId) {
        if (!this.mobileSwiper) return;
        const slides = Array.from(this.mobileSwiper.slides || []);
        const targetIndex = slides.findIndex((slide) => slide.dataset && slide.dataset.mediaId === mediaId);
        if (targetIndex > -1) {
          this.mobileSwiper.slideTo(targetIndex);
        }
      }

      handleMobileSwiperInit(event) {
        if (!event.detail || !event.detail.swiper) return;
        if (this.mobileSwiper === event.detail.swiper) return;
        this.mobileSwiper = event.detail.swiper;
        this.mobileSwiper.on('slideChange', this.handleMobileSwiperSlideChange);
        this.revealMobileScrollTriggers();
      }

      handleMobileSwiperDestroy() {
        if (!this.mobileSwiper) return;
        this.mobileSwiper.off('slideChange', this.handleMobileSwiperSlideChange);
        this.mobileSwiper = null;
      }

      handleMobileSwiperSlideChange() {
        if (!this.mobileSwiper) return;
        const slides = this.mobileSwiper.slides || [];
        const activeSlide = slides[this.mobileSwiper.activeIndex];
        if (!activeSlide || !activeSlide.dataset || !activeSlide.dataset.mediaId) return;
        this.setActiveMedia(activeSlide.dataset.mediaId, false, true);
      }

      revealMobileScrollTriggers() {
        if (!this.mobileSwiperMql.matches || !this.elements.viewer) return;
        this.elements.viewer
          .querySelectorAll('.product__media-item.scroll-trigger')
          .forEach((slide) => {
            slide.classList.add('scroll-trigger--cancel');
            slide.classList.remove('scroll-trigger--offscreen');
            slide.classList.remove('animate--fade-in');
            slide.classList.remove('animate--slide-in');
            slide.classList.remove('scroll-trigger');
          });
      }
    }
  );
}
